import {GenericTable} from "@dvrt/graphql-widgets";
import {defineMessage, defineMessages} from "react-intl";
import {useDomainIDParam} from "@/types";
import {HasPermission, Link} from "../../widgets";
import {IvrListFragment, IvrListFragmentDoc} from "./queries.generated";
import {useBoolState, useConcertoGenericQuery, useDomainNameQuery} from "@/hooks";
import CreateIvrDialog from "@/components/ivr/list/create/CreateIvrDialog";
import FormattedButton from "@/components/FormattedButton";
import {useIvrLimitQuery} from "@/components/ivr/queries.generated";
import {commonTranslations} from "@/utils/translations";
import {ContextFilters} from "@/context/AuthProvider";
import {domainAllocationToQuotaInfo} from "@/components/EndpointCreateDialog";
import {useCallback} from "react";
import TableShortcutButton from "@/components/TableShortcutButton";
import {faTrash} from "@fortawesome/free-solid-svg-icons";

const IvrList = () => {
    const domainId = useDomainIDParam();

    const query = useConcertoGenericQuery<IvrListFragment>("ivrs", IvrListFragmentDoc, {
        filtering: {
            argumentName: "filter",
            placeHolder: commonTranslations.filterPlaceholders.nameOrE164OrShortNumber,
            idPrefix: "search-ivrs",
        },
        fetchPolicy: "no-cache",
        extraVariables: {
            domainId,
        },
        initialOrdering: ["number.shortNumber"],
    });

    const ivrLimitQuery = useIvrLimitQuery({
        variables: {
            domainId: domainId,
        },
        fetchPolicy: "no-cache",
    });

    const {domainName} = useDomainNameQuery(domainId);
    const domainContextFilter = ContextFilters.domain(domainName);

    const [isOpen, setShowOn, setShowOff] = useBoolState();

    const refetch = useCallback(() => {
        query.refetch();
        ivrLimitQuery.refetch();
    }, [ivrLimitQuery, query]);

    return (
        <div style={{padding: "0 20px"}}>
            <GenericTable<IvrListFragment>
                query={query}
                hideColumns={["numberId"]}
                extraColumns={[
                    {
                        path: "actions",
                        label: null,
                        renderer: (row) => {
                            return (
                                <HasPermission name="delete_ivr" contextFilter={domainContextFilter}>
                                    <TableShortcutButton
                                        shortcutPath={`${row.id}/delete`}
                                        tooltipTitle={commonTranslations.delete}
                                        buttonIcon={faTrash}
                                        color="secondary"
                                    />
                                </HasPermission>
                            );
                        },
                    },
                ]}
                labels={defineMessages({
                    number: {id: "number", defaultMessage: "Number"},
                    name: {id: "name", defaultMessage: "Name"},
                    numberId: {id: "unused", defaultMessage: "Unused"},
                    "number.shortNumber": {id: "short_number", defaultMessage: "Short number"},
                    "number.e164": {id: "e164", defaultMessage: "E.164"},
                })}
                dataRenderers={{
                    number: {
                        shortNumber: (value, _, rootRow) => {
                            return <Link to={`/domains/${domainId}/ivrs/${rootRow.id}/settings`}>{value}</Link>;
                        },
                    },
                    name: (value, row) => {
                        return <Link to={`/domains/${domainId}/ivrs/${row.id}/settings`}>{value}</Link>;
                    },
                }}
                createButton={
                    <HasPermission name="add_ivr" contextFilter={domainContextFilter}>
                        <FormattedButton
                            onClick={setShowOn}
                            message={defineMessage({id: "new_ivr", defaultMessage: "New auto attendant"})}
                        />
                    </HasPermission>
                }
                orderableFields={["number.shortNumber", "number.e164", "name"]}
            />
            <HasPermission name="add_ivr" contextFilter={domainContextFilter}>
                <CreateIvrDialog
                    domainId={domainId}
                    isOpen={isOpen}
                    close={setShowOff}
                    onSuccess={refetch}
                    quotaInfo={domainAllocationToQuotaInfo(ivrLimitQuery.data?.domainAllocation.services.ccAattendant)}
                />
            </HasPermission>
        </div>
    );
};

export default IvrList;
